<template>
    <v-form ref="form" v-model="valid">
        <v-row>
            <v-col cols="12">
                <div class="d-flex flex-column flex-md-row">
                    <div v-if="info.communicationId" class="d-flex flex-column">
                        <div class="d-flex">
                            <span> {{ `${$t('created_by')}: ` }} &nbsp; </span>
                            <span> {{ editNoteInfo.created }} &nbsp; </span>
                            <span>
                                {{ editNoteInfo.createdUser }}
                            </span>
                        </div>
                        <div class="d-flex">
                            <span> {{ `${$t('modified_by')}: ` }} &nbsp; </span>
                            <span> {{ editNoteInfo.modified }} &nbsp; </span>
                            <span>
                                {{ editNoteInfo.modifiedUser }}
                            </span>
                        </div>
                    </div>
                    <v-spacer />
                    <v-checkbox v-model="info.active" :label="`${$t('active')}`" />
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-text-field
                    v-if="preset || (info.subject && !!info.communicationId)"
                    v-model="info.subject"
                    :disabled="!preset"
                    :label="`${$t('title')}*`"
                    variant="outlined"
                    persistent-placeholder
                    hide-details
                    :rules="[requiredRule('title')]"
                />
                <v-autocomplete
                    v-else-if="!preset && !info.communicationId"
                    :items="presetItems"
                    :label="$t('select_preset_note')"
                    :custom-filter="presetSearch"
                    item-title="subject"
                    variant="outlined"
                    persistent-placeholder
                    return-object
                    clearable
                    hide-details
                    @update:model-value="presetSelect"
                />
            </v-col>
            <v-col cols="12">
                <keep-alive>
                    <NewHtmlEditor v-model="info.description" :label="`${$t('internal_note', 1)}*`" />
                </keep-alive>
                <v-textarea
                    v-model="info.description"
                    :rules="[requiredRule('note')]"
                    style="visibility: hidden; width: 0; height: 0; position: absolute"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="6">
                <v-select
                    v-if="!specifiedService && !preset"
                    v-model="info.processId"
                    item-title="name"
                    item-value="value"
                    :menu-props="{bottom: true, offsetY: true}"
                    hide-details
                    variant="outlined"
                    :items="relatedToServices"
                    :label="`${$t('relates_to')}*`"
                    :rules="[v => !!v || v === null || `${$t('relates_to')} ${$t('relates_to')}`]"
                />
                <v-expand-transition>
                    <div v-if="personRole !== 'dispatcher' && info.visibleForClients" class="d-flex flex-column">
                        <div class="mt-5 text-subtitle-1">{{ $t('show_in') }}:</div>
                        <v-checkbox v-model="info.showInInvoice" :label="$t('show_note_invoice')" hide-details />
                        <v-checkbox v-model="info.showInQuotation" :label="$t('show_note_quotation')" hide-details />
                    </div>
                </v-expand-transition>
                <template v-if="isTO1">
                    <div class="mt-5 text-subtitle-1">{{ $t('visible_for') }}:</div>
                    <v-checkbox v-model="info.visibleForClients" :label="$t('for_clients')" hide-details />
                    <v-checkbox v-model="info.visibleForSuppliers" :label="$t('for_suppliers')" hide-details />
                    <v-checkbox :model-value="true" disabled :label="$t('for_to1')" hide-details />
                </template>
            </v-col>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, VModel} from 'vue-facing-decorator'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import TimeField from '@/components/snippets/forms/TimeField.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import type {VForm} from 'vuetify/components'

    @Component({
        components: {
            TimeField,
            DatePicker,
            NewHtmlEditor: defineAsyncComponent(() => import('@/components/admin/NewHtmlEditor.vue')),
        },
    })
    export class InternalNoteEditForm extends mixins(FormValidationRulesMixin) {
        @VModel() info
        @Prop() services
        @Prop() specifiedService
        @Prop() editNoteInfo
        @Prop({default: false, type: Boolean}) preset
        @Prop({default: false}) _valid

        presetItems = []

        @Emit()
        changeValid(val) {
            return val
        }

        created() {
            if (!this.info.communicationDateTime) {
                this.info.communicationDateTime = this.$dateFns.format(new Date(), "yyyy-MM-dd'T'HH:mm")
            }
            if (!this.preset && !this.info.processId) {
                this.info.processId = this.relatedToServices[0].value
            }
        }

        async mounted() {
            if (this.preset) return
            try {
                const {communications} = await this.$api.communications.get({
                    type: 'NOTES',
                    presetOnly: true,
                })
                this.presetItems = communications
            } catch (e) {
                this.presetItems = []
            }
        }

        presetSearch(itemTitle, queryText, item) {
            const subject = item.raw.subject,
                description = item.raw.description

            return `${subject.toLowerCase()} ${description.toLowerCase()}`.indexOf(queryText.toLowerCase()) !== -1
        }

        presetSelect(preset) {
            if (preset) {
                const {subject, description, showInInvoice, showInQuotation, visibleForClients, visibleForSuppliers} =
                    preset
                Object.assign(this.info, {
                    subject,
                    description,
                    showInInvoice,
                    showInQuotation,
                    visibleForClients,
                    visibleForSuppliers,
                })
                this.info.description = preset.description
            } else {
                this.info.subject = null
                this.info.description = ''
            }
        }

        async validate() {
            return await (this.$refs.form as VForm).validate()
        }

        get relatedToServices() {
            if (this.specifiedService) {
                const {serviceName, processId} = this.specifiedService
                return [{name: serviceName, value: processId}]
            }
            const itemsByServices = this.services.map(e => ({
                name: `${e.serviceName}${e.roomTypeName ? `, ${e.roomTypeName}` : ''}`,
                value: e.processId,
            }))
            return [{name: this.$t('reservation'), value: null}, ...itemsByServices]
        }

        get personRole() {
            return authStore.personRole
        }

        get isTO1() {
            return authStore.isTO1
        }

        get valid() {
            return this._valid
        }

        set valid(val) {
            this.changeValid(val)
        }
    }

    export default InternalNoteEditForm
</script>
